import { CheckCircleIcon, ChevronDownIcon } from '../../../../../../assets/icons';
import { useExtraServiceGate } from '../../../hooks/useExtraServiceGate';
import { setPlansModalShow } from '../../../../../../store/workspace/slice';
import { selectProfile } from '../../../../../../store/workspace/selectors';
import { useDispatch, useSelector } from '../../../../../../store/store';
import EmptyState from '../../../components/EmptyState';
import { thousandSeparator } from '../../../../../../utils/numberHelpers';
import { UserPlan } from '../../../../../../store/workspace/types';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../../../components/Button';
import { plansEnum } from '../../../../../../configs';
import styles from './style.module.scss';
import { useMemo } from 'react';

interface Props {
    plan: UserPlan;
    isDetailShow: boolean;
    /** Extra services and the plan CTA are only offered on the active plan. */
    isCurrentPlan: boolean;
    toggleDetail: () => void;
};

/** One plan of the history, with the jobs it was used for behind a toggle. */
function PlanCard({ plan, isDetailShow, isCurrentPlan, toggleDetail }: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const profile = useSelector(selectProfile);
    const { addMoreJobPosts, addMoreApplicants } = useExtraServiceGate();

    /** Desktop table and mobile cards render the exact same formatted values. */
    const jobs = useMemo(() => plan.jobList?.map(job => ({
        id: job.id,
        title: job.title,
        applicants: job.applicantCount,
        postedAt: job.lastModified?.slice(0, 10),
        deadline: job.multipleChoiceExamDeadline?.slice(0, 10),
        salary: `${thousandSeparator(job.baseSalaryAmount || 0)}$ /${job.baseSalaryFrequency}`,
    })) || [], [plan.jobList]);

    const navigateToPayment = () => {
        navigate('/workspace/payment');
        dispatch(setPlansModalShow(true));
    };

    return (
        <div className={styles.plan}>
            <span className={styles.plan_name}>
                {plan.level ? `${plansEnum[plan.level]} plan` : ''}
                <b>{` (Started at ${plan.modify?.slice(0, 10)})`}</b>
            </span>

            <div className={styles.plan_detail}>
                <div className={styles.plan_detail_features}>
                    <div className={styles.plan_detail_features_item}>
                        <CheckCircleIcon />
                        <span><b>{plan.jobList?.length}/{plan.planCounter}</b> Job postings per year</span>
                        {isCurrentPlan && <h4 onClick={addMoreJobPosts}>Add More</h4>}
                    </div>
                    <div className={styles.plan_detail_features_item}>
                        <CheckCircleIcon />
                        <span><b>{plan.applicantCount}/{plan.examinerCount}</b> of Applicants per year</span>
                        {isCurrentPlan && <h4 onClick={addMoreApplicants}>Add More</h4>}
                    </div>
                </div>
                <div
                    data-is-show={isDetailShow}
                    onClick={toggleDetail}
                    className={styles.plan_detail_action}
                >
                    <span>{isDetailShow ? 'Hide' : 'See'} details</span>
                    <ChevronDownIcon width={16} height={16} />
                </div>
            </div>

            {isDetailShow && <div className={styles.plan_table}>
                {jobs.length > 0 ? <>
                    <table>
                        <thead>
                            <tr>
                                <th>Order number</th>
                                <th>Job title</th>
                                <th>Salary amount</th>
                                <th>Job post date</th>
                                <th>Job deadline</th>
                                <th>Applicants</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job, index) => <tr key={job.id}>
                                <td>{index + 1}</td>
                                <td>{job.title}</td>
                                <td>{job.salary}</td>
                                <td>{job.postedAt}</td>
                                <td>{job.deadline}</td>
                                <td>{job.applicants}</td>
                            </tr>)}
                        </tbody>
                    </table>

                    <div className={styles.plan_table_cards}>
                        {jobs.map((job, index) => <div
                            key={job.id}
                            className={styles.plan_table_cards_item}
                        >
                            <div className={styles.plan_table_cards_item_header}>
                                <span>Job {index + 1}:</span>
                                <b>{job.title}</b>
                            </div>
                            <div className={styles.plan_table_cards_item_content}>
                                <div className={styles.plan_table_cards_item_content_row}>
                                    <span>Salary amount</span>
                                    <b>{job.salary}</b>
                                </div>
                                <div className={styles.plan_table_cards_item_content_row}>
                                    <span>Job post date</span>
                                    <b>{job.postedAt}</b>
                                </div>
                                <div className={styles.plan_table_cards_item_content_row}>
                                    <span>Job deadline</span>
                                    <b>{job.deadline}</b>
                                </div>
                                <div className={styles.plan_table_cards_item_content_row}>
                                    <span>Applicants</span>
                                    <b>{job.applicants}</b>
                                </div>
                            </div>
                        </div>)}
                    </div>
                </> : <EmptyState />}
            </div>}

            {profile?.userRole === 'owner' && isCurrentPlan && <Button
                text='Change Plan'
                className={styles.plan_CTA}
                onClick={navigateToPayment}
            />}
        </div>
    );
};

export default PlanCard;
